import { useQuery } from "@tanstack/react-query";
import { db, type PackageRecord, type SubscriptionRecord } from "@/lib/billing";
import { isCurrentUserAdmin } from "@/lib/adminUsers";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";

export type ServiceRecord = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_admin_only: boolean;
  is_user_visible: boolean;
  required_integration: string | null;
  feature_flags: Record<string, unknown>;
  limits: Record<string, unknown>;
  included_package_slugs: string[];
  sort_order: number;
};

export type TemplateRecord = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  template_type: string;
  category: string | null;
  target_niche: string | null;
  language: string;
  preview_image_url: string | null;
  demo_url: string | null;
  features: string[];
  default_sections: string[];
  required_package_slug: string | null;
  required_service_slug: string | null;
  is_active: boolean;
  is_exclusive: boolean;
  claimed_by_website_id: string | null;
  claimed_at: string | null;
  exclusivity_edition: "signature" | "numbered" | "unique" | null;
  layout_key: string | null;
  theme: unknown;
  created_at: string;
  updated_at: string;
};

export type ProjectInfoRecord = {
  id: string;
  user_id: string;
  website_id: string | null;
  package_id: string | null;
  template_id: string | null;
  status: string;
  project_name: string;
  language: string;
  category: string;
  domain_preference: string | null;
  subdomain_preference: string | null;
  branding: Record<string, unknown>;
  content_preferences: Record<string, unknown>;
  publishing_preferences: Record<string, unknown>;
  selected_package?: PackageRecord | null;
  selected_template?: TemplateRecord | null;
};

const serviceFeatureMap: Record<string, string> = {
  "ai-article-generation": "ai_article_generation",
  "ai-image-generation": "ai_image_generation",
  "wordpress-publishing": "wordpress_publishing",
  "plesk-hosting": "plesk_provisioning",
  "automation-auto-publishing": "automation",
  "media-library": "media_library",
  "seo-tools": "seo",
  "translation-tools": "translation",
  "api-embed-access": "api_embed",
  "custom-domain": "custom_domain",
  "team-members": "team_members",
  "news-site": "live_publishing",
};

const routeFeatureMap: Record<string, string> = {
  "/websites": "live_publishing",
  "/articles": "live_publishing",
  "/content-factory": "ai_article_generation",
  "/ai-studio": "ai_article_generation",
  "/media-library": "media_library",
  "/published": "live_publishing",
  "/seo": "seo",
  "/translations": "translation",
  "/templates": "templates",
  "/automation": "automation",
  "/admin/autopublish": "automation",
  "/integrations": "api_embed",
  "/domains": "custom_domain",
  "/teams": "team_members",
  "/team": "team_members",
  // Batch-3 routes — credits page and per-org/site model pickers all hang
  // off the AI generation feature; if the package doesn't include any AI
  // generation there is nothing to configure or top up here.
  "/billing/credits": "ai_article_generation",
  "/org/settings/ai-defaults": "ai_article_generation",
};

export function packageIncludesFeature(pkg: PackageRecord | null | undefined, feature: string) {
  if (!pkg) return false;
  if (pkg.is_enterprise) return true;
  const slug = pkg.slug?.replace(/-v2$/, "");
  const v2Features: Record<string, string[]> = {
    trial: [
      "live_publishing",
      "ai_article_generation",
      "ai_image_generation",
      "media_library",
      "seo",
      "translation",
      "templates",
      "automation",
      "custom_domain",
      "team_members",
      "api_embed",
    ],
    starter: ["live_publishing", "ai_article_generation", "media_library", "seo", "templates", "automation"],
    growth: ["live_publishing", "ai_article_generation", "ai_image_generation", "media_library", "seo", "translation", "templates", "automation", "custom_domain", "team_members", "api_embed"],
    network: ["live_publishing", "ai_article_generation", "ai_image_generation", "media_library", "seo", "translation", "templates", "automation", "custom_domain", "team_members", "api_embed"],
    agency: ["live_publishing", "ai_article_generation", "ai_image_generation", "media_library", "seo", "translation", "templates", "automation", "custom_domain", "team_members", "api_embed"],
  };
  if (v2Features[slug]?.includes(feature)) return true;
  if (feature === "templates") return !pkg.is_trial;
  if (feature === "team_members") return (pkg.max_team_members ?? 0) > 1;
  if (feature === "ai_article_generation") return (pkg.max_ai_credits ?? 0) > 0;
  if (feature === "ai_image_generation") return Boolean(pkg.features?.ai_image_generation ?? pkg.features?.media_library ?? pkg.max_media_assets);
  if (feature === "media_library") return (pkg.max_media_assets ?? 0) > 0;
  return Boolean(pkg.features?.[feature]);
}

export function serviceAllowed(pkg: PackageRecord | null | undefined, service: ServiceRecord) {
  if (!pkg) return false;
  if (service.included_package_slugs?.includes(pkg.slug)) return true;
  return Object.entries(serviceFeatureMap).some(([slug, feature]) => service.slug === slug && packageIncludesFeature(pkg, feature));
}

export function templateAllowed(pkg: PackageRecord | null | undefined, template: TemplateRecord) {
  if (!pkg) return false;
  if (pkg.is_enterprise || !template.required_package_slug) return true;
  const order = ["trial", "news-site-starter", "news-site-pro", "media-network", "enterprise"];
  return order.indexOf(pkg.slug) >= order.indexOf(template.required_package_slug);
}

export function featureForRoute(path: string) {
  return Object.entries(routeFeatureMap).find(([route]) => path === route || path.startsWith(`${route}/`))?.[1] ?? null;
}

async function fetchCurrentSubscription(userId: string): Promise<SubscriptionRecord | null> {
  const { data, error } = await db
    .from("user_subscriptions")
    .select("*, packages(*)")
    .eq("user_id", userId)
    .in("status", ["trialing", "active"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function fetchCurrentCompanySubscription(companyId: string, userId: string): Promise<SubscriptionRecord | null> {
  const { data, error } = await db
    .from("user_subscriptions")
    .select("*, packages(*)")
    .eq("company_id", companyId)
    .in("status", ["trialing", "active"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (data) return data;
  return fetchCurrentSubscription(userId);
}

export function usePackageAccess() {
  const { user } = useAuth();
  const { currentCompanyId, isPlatformOwner } = useCompany();
  const { data: subscription, isLoading } = useQuery({
    queryKey: ["package-access", user?.id, currentCompanyId],
    queryFn: () => currentCompanyId ? fetchCurrentCompanySubscription(currentCompanyId, user!.id) : fetchCurrentSubscription(user!.id),
    enabled: !!user,
  });
  const { data: isAdmin = false } = useQuery({
    queryKey: ["package-access-admin", user?.id],
    queryFn: () => isCurrentUserAdmin(user?.id),
    enabled: !!user,
  });

  const pkg = subscription?.packages ?? null;
  return {
    isLoading,
    isAdmin: isAdmin || isPlatformOwner,
    subscription,
    package: pkg,
    can: (feature: string) => isAdmin || isPlatformOwner || packageIncludesFeature(pkg, feature),
    reason: (feature: string) => {
      if (isAdmin || isPlatformOwner || packageIncludesFeature(pkg, feature)) return null;
      if (!pkg) return "No active company package is attached to this workspace.";
      return `The ${pkg.name} package does not include ${feature.replaceAll("_", " ")}.`;
    },
  };
}
